import {
  createSlice,
  createAsyncThunk,
  type PayloadAction,
} from '@reduxjs/toolkit';
import { dynamicAiSuggestionService } from '@/services/dynamicAiSuggestionService';
import type {
  DynamicSuggestionListItem,
  GetSuggestionByIdOutputDTO,
  ArticleTagOutputDTO,
} from '@/types/dynamicSuggestion';

/**
 * Thunk to fetch available tags
 */
export const fetchAvailableTags = createAsyncThunk(
  'dynamicAiSuggestion/fetchAvailableTags',
  async (_, { rejectWithValue }) => {
    try {
      return await dynamicAiSuggestionService.fetchAvailableTags();
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch tags'
      );
    }
  }
);

/**
 * Thunk to fetch suggestions for a contact
 */
export const fetchDynamicAiSuggestions = createAsyncThunk(
  'dynamicAiSuggestion/fetchSuggestions',
  async (
    { contactId, tags }: { contactId: string; tags?: number[] },
    { rejectWithValue }
  ) => {
    try {
      return await dynamicAiSuggestionService.fetchDynamicAiSuggestions(
        contactId,
        tags
      );
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch suggestions'
      );
    }
  }
);

/**
 * Thunk to fetch suggestion details by ID
 */
export const fetchSuggestionById = createAsyncThunk(
  'dynamicAiSuggestion/fetchSuggestionById',
  async (suggestionId: string, { rejectWithValue }) => {
    try {
      return await dynamicAiSuggestionService.fetchSuggestionById(suggestionId);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch suggestion'
      );
    }
  }
);

interface DynamicAiSuggestionState {
  suggestions: DynamicSuggestionListItem[];
  availableTags: ArticleTagOutputDTO[];
  selectedTags: number[];
  loading: boolean;
  fetchingTags: boolean;
  fetchingDetail: boolean;
  error: string | null;
  detailError: string | null;
  contactId: string | null;
  selectedSuggestionDetail: GetSuggestionByIdOutputDTO | null;
}

const initialState: DynamicAiSuggestionState = {
  suggestions: [],
  availableTags: [],
  selectedTags: [],
  loading: false,
  fetchingTags: false,
  fetchingDetail: false,
  error: null,
  detailError: null,
  contactId: null,
  selectedSuggestionDetail: null,
};

const dynamicAiSuggestionSlice = createSlice({
  name: 'dynamicAiSuggestion',
  initialState,
  reducers: {
    /**
     * Update selected tags for filtering
     */
    setSelectedTags: (state, action: PayloadAction<number[]>) => {
      state.selectedTags = action.payload;
    },

    /**
     * Clear all suggestions and reset state
     */
    clearSuggestions: () => initialState,

    /**
     * Set the current contact ID (used for context)
     */
    setContactId: (state, action: PayloadAction<string | null>) => {
      state.contactId = action.payload;
    },

    /**
     * Clear error message
     */
    clearError: state => {
      state.error = null;
    },

    /**
     * Clear detail error
     */
    clearDetailError: state => {
      state.detailError = null;
    },

    /**
     * Clear selected suggestion detail
     */
    clearSelectedDetail: state => {
      state.selectedSuggestionDetail = null;
    },

    /**
     * Reset on contact change
     */
    resetOnContactChange: state => {
      state.suggestions = [];
      state.selectedSuggestionDetail = null;
      state.error = null;
      state.detailError = null;
    },
  },

  extraReducers: builder => {
    // Fetch available tags
    builder
      .addCase(fetchAvailableTags.pending, state => {
        state.fetchingTags = true;
      })
      .addCase(fetchAvailableTags.fulfilled, (state, action) => {
        state.fetchingTags = false;
        // Ensure payload is an array
        state.availableTags = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(fetchAvailableTags.rejected, state => {
        state.fetchingTags = false;
        state.availableTags = [];
      });

    // Fetch suggestions
    builder
      .addCase(fetchDynamicAiSuggestions.pending, state => {
        state.loading = true;
        state.error = null;
        // Don't clear suggestions here to preserve them across loads within same contact
      })
      .addCase(fetchDynamicAiSuggestions.fulfilled, (state, action) => {
        state.loading = false;

        // Handle both direct array and wrapped response
        const responseData = action.payload;
        const newSuggestions = Array.isArray(responseData)
          ? responseData
          : responseData?.suggestions || [];

        // For new contact or new filters, replace suggestions entirely
        // (don't merge to avoid stale data from previous contacts)
        state.suggestions = newSuggestions;
      })
      .addCase(fetchDynamicAiSuggestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch suggestion details by ID
    builder
      .addCase(fetchSuggestionById.pending, state => {
        state.fetchingDetail = true;
        state.detailError = null;
      })
      .addCase(fetchSuggestionById.fulfilled, (state, action) => {
        state.fetchingDetail = false;
        state.selectedSuggestionDetail = action.payload;
      })
      .addCase(fetchSuggestionById.rejected, (state, action) => {
        state.fetchingDetail = false;
        state.detailError = action.payload as string;
      });
  },
});

export const {
  setSelectedTags,
  clearSuggestions,
  setContactId,
  clearError,
  clearDetailError,
  clearSelectedDetail,
  resetOnContactChange,
} = dynamicAiSuggestionSlice.actions;

export default dynamicAiSuggestionSlice.reducer;
